import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode } from "vue"
import _imports_0 from '@/assets/images/logo.png'


const _hoisted_1 = {
  key: 0,
  id: "app"
}
const _hoisted_2 = {
  key: 1,
  class: "small-screen-message"
}

import { onMounted, onBeforeUnmount, computed, watch, ref } from "vue";
import { useStore } from "vuex";
import { GET_TRIAL_STATUS, GET_SUBSCRIPTION_STATUS } from "@/store/action-types";
import NotificationDialog from "@/components/notification/NotificationDialog.vue";
import SocketStatusBanner from "@/components/common/SocketStatusBanner.vue";
import BucketWebhookHandler from "@/components/bucket/BucketWebhookHandler.vue";
import PowerDialerHost from "@/components/dialer/PowerDialerHost.vue";
import IncomingCallModal from "@/components/call/IncomingCallModal.vue";
import MinimizedCallView from "@/components/call/MinimizedCallView.vue";

import TaskFollowupModal from "@/components/bucket/TaskFollowupModal.vue";
import UsageBasedFeaturesModal from "@/components/billing/UsageBasedFeaturesModal.vue";
import InsufficientCreditsModal from "@/components/billing/InsufficientCreditsModal.vue";
import AssistantWidget from "@/components/agent/assistant/AssistantWidget.vue";
import { DIALER_SESSION_ROUTE } from "@/router/routes";
import { useScreenSize } from "@/composables/ui/useScreenSize";
import { useAppStore } from "@/composables/app/useAppStore";
import { useIncomingCall, IncomingCallData } from "@/composables/calls/useIncomingCall";
import { useAIAssistant } from "@/composables/calls/useAIAssistant";
import { usePostCallLock } from "@/composables/calls/usePostCallLock";
import { useToast } from "@/composables/ui/useToast";
import { useGlobalFollowupModal } from "@/composables/bucket/useGlobalFollowupModal";
import { useUnreadCountSync } from "@/composables/messages/useUnreadCountSync";
import { getSocket } from "@/utils/socket";
import Twillio from "@/services/twilio/twilio";
import { useSubscriptionModals } from "@/composables/ui/useSubscriptionModals";
import {
  useGlobalBillingUpdates,
  useSubscriptionAccessGuard,
  invalidateBillingQueries,
} from "@/composables/billing";
import { useRouter } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { bindNotificationSocket } from "@/services/notification/notification-socket";
import { AUTH_ONLY_ROUTES } from "@/router/auth-only-routes";


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const { isLargeScreen } = useScreenSize();
useGlobalBillingUpdates();
useSubscriptionAccessGuard();
useUnreadCountSync();
const { route } = useAppStore();
const router = useRouter();
const queryClient = useQueryClient();
const store = useStore();
const account = computed(() => store.getters.account?.data);
const canShowBillingModals = computed(() => !!account.value?.isEmailVerified);
// The power dialer runs app-wide (not just on /dialer) so the rep can keep
// working while it dials in the background. Unmounting it (logout) hangs up.
const showPowerDialer = computed(
  () => !!account.value?.id && !AUTH_ONLY_ROUTES.has(route.name as string)
);
// The CRM assistant bubble, on every signed-in page. Logging out unmounts it and its chat.
const showAssistant = computed(
  () => !!account.value?.id && !AUTH_ONLY_ROUTES.has(route.name as string)
);
const { usageBasedFeaturesModal, hideUsageBasedFeaturesModal, hideInsufficientCreditsModal } =
  useSubscriptionModals();
const daysLeftInTrial = computed(() => store.getters.daysLeftInTrial);
const hasPaymentMethod = computed(() => store.getters.hasPaymentMethod);
const organizationActiveSubscription = computed(
  () => store.getters.organizationActiveSubscription?.data
);
const hasActiveSubscription = computed(
  () => organizationActiveSubscription.value?.status === "active"
);

if (route.query.creditsPurchased === "true") {
  sessionStorage.setItem("sb_credits_just_purchased", Date.now().toString());
}

const $toast = useToast();

const {
  incomingCall,
  hasIncomingCall,
  callerName,
  callerPhone,
  callState,
  callDuration,
  wasCallAnswered,
  isMinimized,
  transcriptionMessages,
  parentCallSid,
  childCallSid,
  setIncomingCall,
  setCallActive,
  setChildCallSid,
  isCallSidMatch,
  clearIncomingCall,
  clearCallIfMatches,
  stopCallTimer,
  minimizeCall,
  maximizeCall,
  setActiveConnection,
  updatePartialTranscription,
  finalizeTranscriptionMessage,
} = useIncomingCall();

const {
  isEnabled: aiAssistantEnabled,
  isEnabling: aiAssistantEnabling,
  isPreparing: aiAssistantPreparing,
  isWaitingForCall: aiAssistantWaitingForCall,
  briefing: aiAssistantBriefing,
  suggestions: aiAssistantSuggestions,
  currentStreamingText: aiAssistantStreamingText,
  isStreaming: aiAssistantStreaming,
  isThinking: aiAssistantThinking,
  isMinimized: aiAssistantMinimized,
  error: aiAssistantError,
  hasBriefing: aiAssistantHasBriefing,
  hasSuggestions: aiAssistantHasSuggestions,
  latestSuggestion: aiAssistantLatestSuggestion,
  handleBriefing: handleAIBriefing,
  handleThinking: handleAIThinking,
  handleSuggestion: handleAISuggestion,
  disableAssistant: disableAIAssistant,
  minimize: minimizeAIAssistant,
  maximize: maximizeAIAssistant,
} = useAIAssistant();

const { isPostCallLocked, startPostCallLock } = usePostCallLock();

const showFollowupModal = ref(false);
const followupModalBucket = ref<any>(undefined);
const pendingFollowupModal = ref<any>(null);
const isFollowupModalOpen = ref(false);
const followupModalEmailThreadId = ref<string | null>(null);
const followupModalInitialChatId = ref<string | null>(null);

let socket: ReturnType<typeof getSocket> | null = null;
let deviceInitialized = false;

const isKnownCaller = computed(() => hasIncomingCall.value && !!incomingCall.value?.contact);
const isUnknownCaller = computed(() => hasIncomingCall.value && !incomingCall.value?.contact);
const showUnknownCallerModal = computed(() => isUnknownCaller.value && !isMinimized.value);
const showMinimizedView = computed(
  () =>
    isUnknownCaller.value &&
    isMinimized.value &&
    (callState.value === "active" || callState.value === "ringing")
);
const showKnownCallerModal = computed(() => isKnownCaller.value);

const incomingCallBucket = computed(() => {
  if (!incomingCall.value?.contact || !account.value) return undefined;
  return createBucketFromContact(
    incomingCall.value.contact,
    account.value.organizationId,
    account.value.id
  );
});

const incomingCallDataForModal = computed(() => {
  if (!incomingCall.value) return undefined;
  return {
    callSid: parentCallSid.value || incomingCall.value.callSid,
    from: incomingCall.value.from,
    to: incomingCall.value.to,
    callState: callState.value,
    callDuration: callDuration.value,
  };
});

let safetyDismissTimer: ReturnType<typeof setTimeout> | null = null;

const clearSafetyDismissTimer = () => {
  if (safetyDismissTimer) {
    clearTimeout(safetyDismissTimer);
    safetyDismissTimer = null;
  }
};

const handleIncomingCallEvent = async (data: IncomingCallData) => {
  const deviceState = Twillio.getDeviceState();
  // socket payload is untrusted; force org/user to the authenticated account
  setIncomingCall({
    ...data,
    organizationId: account.value?.organizationId ?? data.organizationId,
    userId: account.value?.id ?? data.userId,
  });

  if (deviceState.state !== "registered") {
    const identity =
      deviceState.identity || (account.value?.id ? `user_${account.value.id}` : null);
    if (identity) {
      const recovered = await Twillio.ensureRegistered(identity, 8000);
      if (!recovered) {
        $toast.warning(
          "Twilio connection is recovering. If the call cannot be answered, please refresh the page."
        );
        clearSafetyDismissTimer();
        safetyDismissTimer = setTimeout(() => {
          if (hasIncomingCall.value && callState.value === "ringing") {
            clearIncomingCall();
          }
          safetyDismissTimer = null;
        }, 35000);
      }
    }
  }
};

const handleAnswerCall = async () => {
  try {
    const connection = Twillio.getActiveConnection();
    if (connection) {
      setActiveConnection(connection);
      connection.on("accept", () => setCallActive());
      connection.on("disconnect", () => {
        const callSidForLock =
          parentCallSid.value || childCallSid.value || incomingCall.value?.callSid;
        const contact = incomingCall.value?.contact;
        const answered = clearIncomingCall();
        if (answered && contact) {
          if (callSidForLock) startPostCallLock(callSidForLock);
          showFollowupModalForContact(contact);
        }
      });
      connection.on("cancel", () => clearIncomingCall());
      connection.on("reject", () => clearIncomingCall());
    } else {
      $toast.error("Unable to answer call - Twilio Device not connected. Please refresh the page.");
      return;
    }
    await Twillio.answerIncomingCall();
  } catch (_error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[App.vue] Failed to answer call:", _error);
    }
  }
};

const handleHangupCall = async () => {
  try {
    await Twillio.hangup();
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[App.vue] Failed to hangup call:", error);
    }
  } finally {
    const callSidForLock = parentCallSid.value || childCallSid.value || incomingCall.value?.callSid;
    const contact = incomingCall.value?.contact;
    const answered = clearIncomingCall();
    if (answered && contact) {
      if (callSidForLock) startPostCallLock(callSidForLock);
      showFollowupModalForContact(contact);
    }
  }
};

const handleDeclineCall = async () => {
  try {
    await Twillio.rejectIncomingCall();
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[App.vue] Failed to reject call:", error);
    }
  } finally {
    clearIncomingCall();
  }
};

const handleCloseModal = () => {
  clearIncomingCall();
};

const handleMinimizeCall = () => {
  minimizeCall();
};

const handleMaximizeCall = () => {
  maximizeCall();
};

const handleIncomingCallModalClose = () => {
  const connection = Twillio.getActiveConnection();
  const isConnectionPending = connection?.status() === "pending";

  if (callState.value === "ringing") {
    if (isConnectionPending) return;
    handleDeclineCall();
  } else if (callState.value === "active") {
    handleHangupCall();
  } else {
    clearIncomingCall();
  }
};

const createBucketFromContact = (
  contact: any,
  organizationId: string,
  userId: string,
  taskType = "call"
) => {
  if (!contact) return undefined;

  return {
    id: null,
    contactId: contact.id,
    contact: {
      id: contact.id,
      fname: contact.fname,
      lname: contact.lname,
      email: null,
      phones: contact.phones || [],
      mobileNumber: null,
      directNumber: null,
      jobTitle: null,
      linkedInUrl: null,
      company: null,
    },
    taskType,
    bucketOptionId: null,
    bucketOption: null,
    followUpDate: null,
    organizationId,
    createdById: userId,
  };
};

const showFollowupModalForContact = (contact: any) => {
  if (!contact || !account.value) return;

  const bucket = createBucketFromContact(contact, account.value.organizationId, account.value.id);

  if (isFollowupModalOpen.value) {
    pendingFollowupModal.value = bucket;
    const contactName =
      contact.fname && contact.lname ? `${contact.fname} ${contact.lname}` : "this contact";
    $toast.info(`Follow-up queued for ${contactName}`);
  } else {
    followupModalBucket.value = bucket;
    showFollowupModal.value = true;
    isFollowupModalOpen.value = true;
  }
};

const handleFollowupModalClose = () => {
  showFollowupModal.value = false;
  followupModalBucket.value = null;
  isFollowupModalOpen.value = false;
  followupModalEmailThreadId.value = null;
  followupModalInitialChatId.value = null;

  if (pendingFollowupModal.value) {
    followupModalBucket.value = pendingFollowupModal.value;
    showFollowupModal.value = true;
    isFollowupModalOpen.value = true;
    pendingFollowupModal.value = null;
  }
};

const {
  pendingRequest,
  consumeRequest,
  setModalOpen: setFollowupModalOpenShared,
} = useGlobalFollowupModal();

// Mirrors whether either TaskFollowupModal instance is actually on screen into
// the shared composable, so other parts of the app (the power dialer banner)
// can react without needing to own or duplicate this modal's state.
watch(
  [isFollowupModalOpen, showKnownCallerModal, () => followupModalBucket.value?.contactId],
  ([followupOpen, knownCallerOpen, followupContactId]) => {
    // The incoming-call modal takes over the screen when both are open, so
    // only report the regular modal's contact when it's the one showing.
    const visibleContactId = followupOpen && !knownCallerOpen ? followupContactId || null : null;
    setFollowupModalOpenShared(followupOpen || knownCallerOpen, visibleContactId);
  },
  { immediate: true }
);

watch(pendingRequest, (req) => {
  if (!req || !account.value) return;
  const request = consumeRequest();
  if (!request) return;

  const contact = {
    id: request.contactId,
    fname: request.contactFname || "",
    lname: request.contactLname || "",
  };

  const bucket = createBucketFromContact(
    contact,
    account.value.organizationId,
    account.value.id,
    request.taskType || "call"
  );

  followupModalEmailThreadId.value = request.emailThreadId || null;
  followupModalInitialChatId.value = request.chatId || null;

  if (request.replace) {
    // Swap immediately — the :key on TaskFollowupModal forces a clean remount
    // for the new contact instead of reusing the previous one's local state.
    pendingFollowupModal.value = null;
    followupModalBucket.value = bucket;
    showFollowupModal.value = true;
    isFollowupModalOpen.value = true;
  } else if (isFollowupModalOpen.value) {
    pendingFollowupModal.value = bucket;
    const name =
      [request.contactFname, request.contactLname].filter(Boolean).join(" ") || "this contact";
    $toast.info(`Follow-up queued for ${name}`);
  } else {
    followupModalBucket.value = bucket;
    showFollowupModal.value = true;
    isFollowupModalOpen.value = true;
  }
});

const handleUsageBasedFeaturesModalClose = () => {
  hideUsageBasedFeaturesModal();
};

const handleProceed = () => {
  const onProceed = usageBasedFeaturesModal.onProceed;
  if (typeof onProceed === "function") onProceed();
};

const handlePurchaseCredits = () => {
  const onPurchaseCredits = usageBasedFeaturesModal.onPurchaseCredits;
  if (typeof onPurchaseCredits === "function") onPurchaseCredits();
};

let captionHandler: ((...args: any[]) => void) | undefined;

const initializeTwilioDevice = async () => {
  if (deviceInitialized || !account.value?.id || !hasActiveSubscription.value) {
    return;
  }
  if (AUTH_ONLY_ROUTES.has(route.name as string)) {
    return;
  }

  try {
    const identity = `user_${account.value.id}`;
    await Twillio.initTwilio(identity);
    deviceInitialized = true;
  } catch (error: any) {
    if (process.env.NODE_ENV === "development") {
      console.error("[App.vue] Failed to initialize Twilio Device:", error);
    }
  }
};

watch(
  canShowBillingModals,
  (canShow) => {
    if (!canShow) {
      hideUsageBasedFeaturesModal();
      hideInsufficientCreditsModal();
    }
  },
  { immediate: true }
);

watch(
  [() => account.value?.id, hasActiveSubscription],
  ([accountId, isActive]: [any, any]) => {
    if (accountId && isActive && !deviceInitialized) {
      initializeTwilioDevice();
    }
  },
  { immediate: true }
);

const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  if (isPostCallLocked.value) {
    e.preventDefault();
    e.returnValue = "";
  }
};

onMounted(() => {
  window.addEventListener("beforeunload", handleBeforeUnload);

  // Initialize notification socket binding (must be always active, not just when dialog is mounted)
  bindNotificationSocket(store);

  // Clear stale modal state rehydrated from localStorage by vuex-persistedstate
  hideUsageBasedFeaturesModal();
  hideInsufficientCreditsModal();

  const refreshBillingState = () => {
    if (!account.value?.isEmailVerified) return;
    store.dispatch(GET_TRIAL_STATUS, { forceRefresh: true });
    store.dispatch(GET_SUBSCRIPTION_STATUS, { forceRefresh: true });
  };

  window.addEventListener("pageshow", (event) => {
    if (event.persisted && sessionStorage.getItem("sb_stripe_redirect_pending")) {
      sessionStorage.removeItem("sb_stripe_redirect_pending");
      hideUsageBasedFeaturesModal();
      hideInsufficientCreditsModal();
    }
    refreshBillingState();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refreshBillingState();
  });

  if (route.query.creditsPurchased === "true") {
    invalidateBillingQueries(queryClient);
    $toast?.success?.("Credits purchased successfully!");
    router.replace({ query: { ...route.query, creditsPurchased: undefined } });
  }

  try {
    socket = getSocket();

    socket.on("call:incoming", handleIncomingCallEvent);

    captionHandler = (data: any) => {
      if (!data.callSid) {
        return;
      }

      if (!isCallSidMatch(data.callSid)) {
        return;
      }

      if (data.isFinal) {
        finalizeTranscriptionMessage(data.role || "Customer", data.text, data.segmentId);
      } else {
        updatePartialTranscription(data.role || "Customer", data.text, data.segmentId);
      }
    };
    socket.on("call:caption", captionHandler);

    // AI Assistant socket listeners
    socket.on("call:ai-assist:briefing", handleAIBriefing);
    socket.on("call:ai-assist:thinking", handleAIThinking);
    socket.on("call:ai-assist:suggestion", handleAISuggestion);

    socket.on("call:status", (payload: any) => {
      const callSid = payload.callSid || payload.parentCallSid;
      if (callSid && incomingCall.value && !isCallSidMatch(callSid)) return;

      // Track child callSid for outbound calls (enables caption matching)
      if (payload.callSid && payload.parentCallSid && payload.callSid !== payload.parentCallSid) {
        if (isCallSidMatch(payload.parentCallSid) && !isCallSidMatch(payload.callSid)) {
          setChildCallSid(payload.callSid);
        }
      }

      if (
        (payload.status === "answered" || payload.status === "in-progress") &&
        callState.value === "ringing"
      ) {
        setCallActive();
      } else if (
        ["completed", "disconnected", "canceled", "no-answer", "busy", "failed"].includes(
          payload.status
        )
      ) {
        disableAIAssistant();
        const callSidForLock =
          parentCallSid.value || childCallSid.value || incomingCall.value?.callSid;
        const contact = incomingCall.value?.contact;
        const answered = clearIncomingCall();
        if (answered && contact) {
          if (callSidForLock) startPostCallLock(callSidForLock);
          showFollowupModalForContact(contact);
        }
      }
    });

    if (account.value?.id) {
      initializeTwilioDevice().then(() => {
        Twillio.onIncomingCallAccept(() => setCallActive());

        Twillio.onIncomingCallDisconnect(() => {
          disableAIAssistant();
          const callSidForLock =
            parentCallSid.value || childCallSid.value || incomingCall.value?.callSid;
          const contact = incomingCall.value?.contact;
          const answered = clearIncomingCall();
          if (answered && contact) {
            if (callSidForLock) startPostCallLock(callSidForLock);
            showFollowupModalForContact(contact);
          }
        });

        Twillio.onIncomingCallCancel(() => {
          disableAIAssistant();
          clearIncomingCall();
        });

        Twillio.onIncomingConnection((connection: any) => {
          clearSafetyDismissTimer();
          const deviceCallSid = connection.parameters?.CallSid;
          if (deviceCallSid) setChildCallSid(deviceCallSid);
          setActiveConnection(connection);

          connection.on("accept", () => setCallActive());
          connection.on("disconnect", () => {
            disableAIAssistant();
            const callSidForLock =
              parentCallSid.value || childCallSid.value || incomingCall.value?.callSid;
            const contact = incomingCall.value?.contact;
            const answered = clearIncomingCall();
            if (answered && contact) {
              if (callSidForLock) startPostCallLock(callSidForLock);
              showFollowupModalForContact(contact);
            }
          });
          connection.on("cancel", () => {
            disableAIAssistant();
            stopCallTimer();
            clearIncomingCall();
          });
          connection.on("reject", () => {
            disableAIAssistant();
            stopCallTimer();
            clearIncomingCall();
          });
          connection.on("error", () => {
            disableAIAssistant();
            stopCallTimer();
            clearIncomingCall();
          });
        });
      });
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[App.vue] Failed to set up Socket.IO listener:", error);
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", handleBeforeUnload);
  stopCallTimer();
  clearSafetyDismissTimer();
  if (socket) {
    socket.off("call:incoming", handleIncomingCallEvent);
    socket.off("call:status");
    socket.off("call:caption", captionHandler);
    socket.off("call:ai-assist:briefing", handleAIBriefing);
    socket.off("call:ai-assist:thinking", handleAIThinking);
    socket.off("call:ai-assist:suggestion", handleAISuggestion);
  }
  disableAIAssistant();
  Twillio.clearIncomingCallCallbacks();
});

return (_ctx: any,_cache: any) => {
  const _component_ToastContainer = _resolveComponent("ToastContainer")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_unref(isLargeScreen)())
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(SocketStatusBanner),
        _createVNode(_component_ToastContainer),
        _createVNode(NotificationDialog),
        _createVNode(BucketWebhookHandler),
        (showPowerDialer.value)
          ? (_openBlock(), _createBlock(PowerDialerHost, { key: 0 }))
          : _createCommentVNode("", true),
        (_openBlock(), _createBlock(_component_router_view, {
          key: _unref(route).path
        })),
        _createVNode(IncomingCallModal, {
          show: showUnknownCallerModal.value,
          "caller-name": _unref(callerName) || undefined,
          "caller-phone-number": _unref(callerPhone) || undefined,
          "call-sid": _unref(parentCallSid) || undefined,
          "contact-id": _unref(incomingCall)?.contact?.id,
          "call-state": _unref(callState),
          "call-duration": _unref(callDuration),
          "is-minimized": _unref(isMinimized),
          onAnswer: handleAnswerCall,
          onDecline: handleDeclineCall,
          onHangup: handleHangupCall,
          onClose: handleCloseModal,
          onMinimize: handleMinimizeCall
        }, null, 8, ["show", "caller-name", "caller-phone-number", "call-sid", "contact-id", "call-state", "call-duration", "is-minimized"]),
        _createVNode(MinimizedCallView, {
          show: showMinimizedView.value,
          "caller-name": _unref(callerName) || undefined,
          "caller-phone-number": _unref(callerPhone) || undefined,
          "call-duration": _unref(callDuration),
          "call-state": _unref(callState),
          "transcription-messages": _unref(transcriptionMessages),
          onAnswer: handleAnswerCall,
          onDecline: handleDeclineCall,
          onHangup: handleHangupCall,
          onMaximize: handleMaximizeCall
        }, null, 8, ["show", "caller-name", "caller-phone-number", "call-duration", "call-state", "transcription-messages"]),
        (showKnownCallerModal.value)
          ? (_openBlock(), _createBlock(TaskFollowupModal, {
              key: 1,
              selectedBucket: incomingCallBucket.value,
              incomingCallData: incomingCallDataForModal.value,
              onCloseTaskFollowupModal: handleIncomingCallModalClose,
              onRefreshBuckets: handleIncomingCallModalClose
            }, null, 8, ["selectedBucket", "incomingCallData"]))
          : _createCommentVNode("", true),
        (showFollowupModal.value && followupModalBucket.value && !showKnownCallerModal.value)
          ? (_openBlock(), _createBlock(TaskFollowupModal, {
              key: followupModalBucket.value?.contactId,
              selectedBucket: followupModalBucket.value,
              initialEmailThreadId: followupModalEmailThreadId.value,
              initialChatId: followupModalInitialChatId.value,
              onCloseTaskFollowupModal: handleFollowupModalClose
            }, null, 8, ["selectedBucket", "initialEmailThreadId", "initialChatId"]))
          : _createCommentVNode("", true),
        (canShowBillingModals.value)
          ? (_openBlock(), _createBlock(UsageBasedFeaturesModal, {
              key: 3,
              show: _unref(usageBasedFeaturesModal).show,
              "has-proceed-action": !!_unref(usageBasedFeaturesModal).onProceed,
              "has-payment-method": hasPaymentMethod.value,
              "balance-cents": _unref(usageBasedFeaturesModal).balanceCents,
              "trial-days-remaining": daysLeftInTrial.value,
              onClose: handleUsageBasedFeaturesModalClose,
              onProceed: handleProceed,
              onPurchaseCredits: handlePurchaseCredits
            }, null, 8, ["show", "has-proceed-action", "has-payment-method", "balance-cents", "trial-days-remaining"]))
          : _createCommentVNode("", true),
        (canShowBillingModals.value)
          ? (_openBlock(), _createBlock(InsufficientCreditsModal, { key: 4 }))
          : _createCommentVNode("", true),
        (showAssistant.value)
          ? _withDirectives((_openBlock(), _createBlock(AssistantWidget, { key: 5 }, null, 512)), [
              [_vShow, _unref(route).name !== _unref(DIALER_SESSION_ROUTE)]
            ])
          : _createCommentVNode("", true)
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[0] || (_cache[0] = [
        _createElementVNode("div", { class: "small-screen-message__card" }, [
          _createElementVNode("img", {
            src: _imports_0,
            alt: "SalesBuckets",
            class: "small-screen-message__logo"
          }),
          _createElementVNode("h6", { class: "small-screen-message__title" }, "Screen Size Not Supported"),
          _createElementVNode("p", { class: "small-screen-message__text" }, " SalesBuckets works best on tablets (landscape) and desktops. Please rotate your device or use a larger screen. ")
        ], -1)
      ]))]))
}
}

})